import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import PhoneInput from "react-phone-number-input";

import "@fortawesome/fontawesome-free/css/all.min.css";
import "react-phone-number-input/style.css";
import "../style/SignUp.css";

import {
  validateField,
  validateForm,
  FormErrors,
} from "../utils/utils";
import { apiService } from "../services/api";
import { useAppDispatch } from "../store/hooks";
import { fetchUserProfile } from "../store/authSlice";

const SettingsPopUp = ({ setProfileSettingsIsOpen, user }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    workEmail: "",
    phoneNumber: "",
    companyName: "",
    title: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // FUB Connection state
  const [fubConnection, setFubConnection] = useState(null);
  const [fubLoading, setFubLoading] = useState(true);
  const [fubDisconnecting, setFubDisconnecting] = useState(false);

  // Load profile data and FUB connection on component mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const profileData = await apiService.getProfile();
        
        setFormData({
          firstName: profileData.firstName || "",
          lastName: profileData.lastName || "",
          workEmail: profileData.email || "",
          phoneNumber: profileData.phoneNumber || "",
          companyName: profileData.companyName || "",
          title: profileData.title || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } catch (error) {
        console.error('Failed to load profile:', error);
        setSaveError('Failed to load profile data');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    const loadFubConnection = async () => {
      try {
        setFubLoading(true);
        const connection = await apiService.getFubConnection();
        setFubConnection(connection);
      } catch (error) {
        console.error('Failed to load FUB connection:', error);
      } finally {
        setFubLoading(false);
      }
    };

    loadProfile();
    loadFubConnection();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleBlur = (field) => {
    const value = formData[field];

    if (field === "currentPassword") {
      // For current password, just check if it's provided when new password is also provided
      // The actual validation will happen on the server when saving
      const error = formData.newPassword && !value ? "Current password is required to change password" : "";
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    } else if (field === "newPassword" || field === "confirmPassword") {
      let error = validateField(field, value);
      if (!error && formData.newPassword && formData.confirmPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          error = "Passwords do not match";
        }
      }
      setErrors((prev) => ({
        ...prev,
        [field]: error,
        confirmPassword:
          field === "confirmPassword"
            ? error
            : formData.newPassword &&
              formData.confirmPassword &&
              formData.newPassword !== formData.confirmPassword
            ? "Passwords do not match"
            : "",
      }));
    } else {
      const error = validateField(field, value);
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    }
  };

  const isFormValid = () => {
    const hasRequiredFields = formData.firstName &&
      formData.lastName &&
      formData.workEmail &&
      formData.phoneNumber &&
      formData.companyName &&
      formData.title;
    
    const hasValidPasswords = !formData.newPassword || // No new password is fine
      (formData.newPassword && formData.currentPassword && formData.confirmPassword && 
       formData.newPassword === formData.confirmPassword && 
       errors.confirmPassword !== "Passwords do not match");
    
    return hasRequiredFields && hasValidPasswords;
  };

  const handleSave = async () => {
    if (!validateForm(formData, setErrors)) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Update profile data
      const profileUpdateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        companyName: formData.companyName,
        title: formData.title,
      };

      await apiService.updateProfile(profileUpdateData);

      // Update password if provided
      if (formData.currentPassword && formData.newPassword) {
        console.log('Attempting to change password...');
        await apiService.changePassword({
          currentPassword: formData.currentPassword.trim(),
          newPassword: formData.newPassword.trim(),
        });
        console.log('Password changed successfully');
      }

      // Refresh the user profile in Redux store
      dispatch(fetchUserProfile());

      setSaveSuccess(true);
      
      // Close the modal after a short delay to show success message
      setTimeout(() => {
        setProfileSettingsIsOpen(false);
      }, 1500);

    } catch (error: any) {
      console.error('Failed to save profile:', error);
      
      // Handle specific password validation errors
      if (error?.response?.data?.message?.includes('password') || 
          error?.response?.data?.message?.includes('Password')) {
        setSaveError('Current password is incorrect. Please check and try again.');
      } else {
        setSaveError(error?.response?.data?.message || 'Failed to save profile. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFubDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your FUB account? This will stop data synchronization.')) {
      return;
    }

    try {
      setFubDisconnecting(true);
      await apiService.disconnectFub();
      setFubConnection({ ...fubConnection, isConnected: false });
      setSaveSuccess(true);
      setSaveError(null);
    } catch (error) {
      console.error('Failed to disconnect FUB:', error);
      setSaveError('Failed to disconnect FUB account');
    } finally {
      setFubDisconnecting(false);
    }
  };

  const handleFubReconnect = () => {
    navigate('/connect-fub');
  };

  return (
    <div
      className="fixed inset-0 bg-[#11141840]/40 flex items-end justify-end z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setProfileSettingsIsOpen(false);
        }
      }}
    >
      <div
        className="bg-lightestGray w-[600px] h-full shadow-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-lighterGray">
          <h2 className="text-h1 font-bold text-darkestSlate">
            Profile Settings
          </h2>
          <button
            onClick={() => setProfileSettingsIsOpen(false)}
            className="text-darkestSlate text-2xl font-light hover:text-deepBlack"
          >
            ×
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {isLoadingProfile ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-spinner fa-spin text-darkBlue text-xl"></i>
                <span className="text-darkGray text-lg">Loading profile...</span>
              </div>
            </div>
          ) : (
            <>
              {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-exclamation-triangle text-red-500"></i>
                    <span className="text-red-700 text-sm">{saveError}</span>
                  </div>
                </div>
              )}
              
              {saveSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-check-circle text-green-500"></i>
                    <span className="text-green-700 text-sm">Profile updated successfully!</span>
                  </div>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-h4 font-bold text-darkGray mb-2">
                  First Name*
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  onBlur={() => handleBlur("firstName")}
                  className={`w-full h-10 px-3 py-2.5 rounded-xl bg-lightestGray border focus:outline-none ${
                    errors.firstName
                      ? "border-red-500"
                      : "border-lighterGray focus:border-darkBlue"
                  }`}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-h4 font-bold text-darkGray mb-2">
                  Last Name*
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  onBlur={() => handleBlur("lastName")}
                  className={`w-full h-10 px-3 py-2.5 rounded-xl bg-lightestGray border focus:outline-none ${
                    errors.lastName
                      ? "border-red-500"
                      : "border-lighterGray focus:border-darkBlue"
                  }`}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-h4 font-bold text-darkGray mb-2">
                Work Email *
              </label>
              <input
                type="email"
                value={formData.workEmail}
                onChange={(e) => handleInputChange("workEmail", e.target.value)}
                onBlur={() => handleBlur("workEmail")}
                className={`w-full h-10 px-3 py-2.5 rounded-xl bg-lightestGray border focus:outline-none ${
                  errors.workEmail
                    ? "border-red-500"
                    : "border-lighterGray focus:border-darkBlue"
                }`}
              />
              {errors.workEmail && (
                <p className="text-red-500 text-xs mt-1">{errors.workEmail}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-h4 font-bold text-darkGray mb-2">
                Phone Number *
              </label>
              <div
                className={
                  errors.phoneNumber ? "border border-red-500 rounded-xl" : ""
                }
              >
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={formData.phoneNumber}
                  onChange={(value) =>
                    handleInputChange(
                      "phoneNumber",
                      (value || "").replace(/\s+/g, "")
                    )
                  }
                  onBlur={() => handleBlur("phoneNumber")}
                  placeholder="Phone Number"
                />
              </div>
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.phoneNumber}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-h4 font-bold text-darkGray mb-2">
                  Company Name*
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) =>
                    handleInputChange("companyName", e.target.value)
                  }
                  onBlur={() => handleBlur("companyName")}
                  className={`w-full h-10 px-3 py-2.5 rounded-xl bg-lightestGray border focus:outline-none ${
                    errors.companyName
                      ? "border-red-500"
                      : "border-lighterGray focus:border-darkBlue"
                  }`}
                />
                {errors.companyName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.companyName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-h4 font-bold text-darkGray mb-2">
                  Title*
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  onBlur={() => handleBlur("title")}
                  className={`w-full h-10 px-3 py-2.5 rounded-xl bg-lightestGray border focus:outline-none ${
                    errors.title
                      ? "border-red-500"
                      : "border-lighterGray focus:border-darkBlue"
                  }`}
                />
                {errors.title && (
                  <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                )}
              </div>
            </div>

            {/* FUB Connection Section */}
            <div className="bg-lightestGray rounded-xl p-4 mb-6 border border-lightGray">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-h4 font-bold text-darkGray mb-1">
                    FUB Connection
                  </label>
                  <p className="text-xs text-gray-600">
                    Follow Up Boss integration status
                  </p>
                </div>
                {fubLoading ? (
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-spinner fa-spin text-darkBlue"></i>
                    <span className="text-sm text-darkGray">Loading...</span>
                  </div>
                ) : fubConnection?.isConnected ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-600 font-medium">Connected</span>
                    </div>
                    <button
                      onClick={handleFubDisconnect}
                      disabled={fubDisconnecting}
                      className="px-3 py-1 text-xs border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      {fubDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-red-600 font-medium">Not Connected</span>
                    </div>
                    <button
                      onClick={handleFubReconnect}
                      className="px-3 py-1 text-xs border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50"
                    >
                      Connect FUB
                    </button>
                  </div>
                )}
              </div>
              {fubConnection?.isConnected && !fubLoading && (
                <div className="text-xs text-gray-600 space-y-1">
                  <p>Account ID: {fubConnection.accountId ? `***${fubConnection.accountId.slice(-4)}` : 'Hidden'}</p>
                  <p>Connected: {fubConnection.connectedAt ? new Date(fubConnection.connectedAt).toLocaleDateString() : 'Unknown'}</p>
                </div>
              )}
            </div>

            <div className="bg-lightestGray rounded-xl p-4 mb-6 border border-lightGray">
              <div className="mb-4">
                {/* <div className="flex items-center justify-between mb-2">
                  <label className="block text-h4 font-bold text-darkGray">
                    Change Password (Optional)
                  </label>
                  <span className="text-xs text-gray-500">Leave blank to keep current password</span>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  If you want to change your password, enter your current password and choose a new one. 
                  Make sure you're entering the correct current password.
                </p> */}
                <label className="block text-h4 font-bold text-darkGray mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.currentPassword}
                    onChange={(e) =>
                      handleInputChange("currentPassword", e.target.value)
                    }
                    onBlur={() => handleBlur("currentPassword")}
                    className={`w-full h-10 px-3 py-2.5 pr-10 rounded-xl bg-lightestGray border focus:outline-none ${
                      errors.currentPassword
                        ? "border-red-500"
                        : "border-lighterGray focus:border-darkBlue"
                    }`}
                    placeholder="Password"
                  />

                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-darkGray hover:text-darkestSlate"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    <i
                      className={`fas ${
                        !showCurrentPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                    ></i>
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.currentPassword}
                  </p>
                )}
                <a
                  href="/"
                  className="text-darkBlue font-bold text-h4 hover:underline"
                >
                  Forgot Password?
                </a>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-h4 font-bold text-darkGray mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      disabled={!!errors.currentPassword}
                      type={showNewPassword ? "text" : "password"}
                      value={formData.newPassword}
                      onChange={(e) =>
                        handleInputChange("newPassword", e.target.value)
                      }
                      onBlur={() => handleBlur("newPassword")}
                      className={`w-full h-10 px-3 py-2.5 pr-10 rounded-xl bg-lightestGray border focus:outline-none ${
                        errors.currentPassword
                          ? "bg-lightGrayCustom border-lighterGray cursor-not-allowed"
                          : errors.confirmPassword === "Passwords do not match"
                          ? "border-red-500"
                          : "border-lighterGray focus:border-darkBlue"
                      }`}
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-darkGray hover:text-darkestSlate"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      <i
                        className={`fas ${
                          !showNewPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      ></i>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-h4 font-bold text-darkGray mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      disabled={!!errors.currentPassword}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                      onBlur={() => handleBlur("confirmPassword")}
                      className={`w-full h-10 px-3 py-2.5 pr-10 rounded-xl bg-lightestGray border focus:outline-none ${
                        errors.currentPassword
                          ? "bg-lightGrayCustom border-lighterGray cursor-not-allowed"
                          : errors.confirmPassword === "Passwords do not match"
                          ? "border-red-500"
                          : "border-lighterGray focus:border-darkBlue"
                      }`}
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-darkGray hover:text-darkestSlate"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <i
                        className={`fas ${
                          !showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      ></i>
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            </div>
              </form>

              {/* Danger Zone: Delete Account */}
              <div className="mt-8 border-t border-lighterGray pt-6">
                <h3 className="text-h2 font-semibold text-darkestSlate mb-2">Delete Account</h3>
                <p className="text-darkGray mb-4">This will permanently delete your account and all associated data. This action cannot be undone.</p>
                {deleteError && (
                  <div className="bg-red-50 text-red-700 border border-red-200 rounded px-3 py-2 mb-3">
                    {deleteError}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    className="flex-1 border border-lighterGray rounded px-3 py-2 outline-none"
                  />
                  <button
                    disabled={deleting || confirmText !== 'DELETE'}
                    onClick={async () => {
                      setDeleteError(null);
                      setDeleting(true);
                      try {
                        await apiService.deleteAccount();
                        localStorage.clear();
                        navigate('/sign_in');
                      } catch (e: any) {
                        setDeleteError(e?.response?.data?.message || 'Failed to delete account');
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    className={`px-4 py-2 rounded text-white ${deleting || confirmText !== 'DELETE' ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    {deleting ? 'Deleting…' : 'Delete Account'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-lighterGray p-6">
          {/* Consent notice and opt-out link */}
          <div className="mb-4">
            <p className="text-xs text-gray-600">
              You agreed to receive texts from MyHomey about homes.{' '}
              <a
                href="/opt-out"
                className="text-darkBlue font-semibold hover:underline"
              >
                Please click here to opt out
              </a>
              .
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              If the link is unavailable, email <a className="hover:underline text-darkBlue" href="mailto:support@myhomey.ai">support@myhomey.ai</a> to opt out.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setProfileSettingsIsOpen(false)}
              type="button"
              className="px-6 py-2 border border-deepBlack text-deepBlack rounded-3xl hover:bg-lightestGray text-h3 font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              type="button"
              disabled={!isFormValid() || isSaving || isLoadingProfile}
              className={`px-6 py-2 rounded-3xl text-h3 font-semibold flex items-center gap-2 ${
                isFormValid() && !isSaving && !isLoadingProfile
                  ? "bg-darkBlue text-lightestGray hover:bg-mediumGray"
                  : "bg-gray text-lightestGray cursor-not-allowed"
              }`}
            >
              {isSaving && <i className="fa-solid fa-spinner fa-spin"></i>}
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPopUp;
